import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useNavContext } from "@/app/auth/useNavContext";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { notificationsSocketService } from "@/modules/dashboard/notifications/services/notifications-socket.service";
import { PaymentsPayinService } from "@/modules/dashboard/payins/services/paymentsPayin.service";
import {
  isIncomingPaymentCandidate,
  keyPaymentFor,
} from "@/shared/components/payments/breb-key/brebKeyIncomingPayments";
import useTranslations from "@/shared/lang/useTranslations";

import { claimAnnouncement, isOnScreen, screenTokens } from "./dashboardAlertAnnouncements";
import { availableDashboardAlertTypes } from "./dashboardAlertAvailability";
import { DASHBOARD_ALERT_CATALOG } from "./dashboardAlertCatalog";
import {
  announcementIdFor,
  brebKeyPaymentAlert,
  cardActivityAlert,
  collectionPaidAlert,
  type DashboardAlert,
  type DashboardAlertsT,
  payoutAlert,
  payoutBatchAlert,
} from "./dashboardAlertEvents";
import { createDashboardAlertPresenter, useUnlockDashboardAlertSound } from "./dashboardAlertPresenter";

/**
 * How long an alert waits before claiming its announcement: a screen that shows the same thing marks it as
 * soon as it reads it, and a visible tab goes before a hidden one, so only one place tells, and only one tab
 * chimes.
 */
const CLAIM_DELAY_VISIBLE_MS = 700;
const CLAIM_DELAY_HIDDEN_MS = 1_500;

/**
 * The dashboard's corner alerts, mounted once in `DashboardLayout`: incoming payments (Bre-B keys and paid
 * collections), payouts paid or refused, payout batches finished, and card purchases, declines and refunds.
 * Each is shown only when its switch is on (Settings → Avisos en el dashboard), the person can read that
 * module, and no screen already shows it (the static QR of that key, the result screen of that payin or
 * payout, the page of that batch).
 *
 * Pushes the backend marks `announce: false` are skipped: batch rows, USDT bought or sold on the desk, the
 * same ones the WhatsApp alerts leave out.
 *
 * Organization members only: staff receive every organization's pushes. Socket only, no polling, because it
 * runs on every screen; a push lost while the connection is down shows no alert, and the operation is in its
 * list as always. Bre-B payins are read once (`GET /payins/:id`), since only the detail says whether the
 * money came through a key; every other alert is built from the push itself.
 */
export function useDashboardAlerts(): void {
  const { user, isInternalUser } = useAuth();
  const navContext = useNavContext();
  const member = !!user && !isInternalUser;
  // What the sidebar shows this person decides which alerts exist (`dashboardAlertAvailability`).
  const available = member ? availableDashboardAlertTypes(navContext, { brebKeys: null }) : [];
  const availableKey = available.join(",");
  const payments = available.some(
    (type) =>
      DASHBOARD_ALERT_CATALOG[type].group === "payins" || DASHBOARD_ALERT_CATALOG[type].group === "payouts",
  );
  const payoutBatches = available.includes("payout_batches_done");
  const cards = available.some((type) => DASHBOARD_ALERT_CATALOG[type].group === "cards");
  const navigate = useNavigate();
  const { t, activeLanguage } = useTranslations("dashboardAlerts");
  useUnlockDashboardAlertSound(payments || payoutBatches || cards);

  // Read when an alert shows, so a new language never resubscribes the socket.
  const latest = useRef({ navigate, t: t as DashboardAlertsT, language: activeLanguage });
  useEffect(() => {
    latest.current = { navigate, t: t as DashboardAlertsT, language: activeLanguage };
  });

  useEffect(() => {
    if (!payments && !payoutBatches && !cards) return;
    const present = createDashboardAlertPresenter(() => ({
      t: latest.current.t,
      language: latest.current.language,
      navigate: (href) => latest.current.navigate(href),
    }));
    const text = () => ({ t: latest.current.t, language: latest.current.language });
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const readBrebPayins = new Set<string>();
    const unsubscribes: (() => void)[] = [];
    let alive = true;

    const availableTypes = new Set(availableKey.split(","));
    const announce = (alert: DashboardAlert | null, tokens: readonly string[]) => {
      if (!alert || !availableTypes.has(alert.type)) return;
      const timer = setTimeout(
        () => {
          timers.delete(timer);
          if (!alive || isOnScreen(tokens)) return;
          void claimAnnouncement(announcementIdFor(alert.type, alert.id)).then((claimed) => {
            if (claimed && alive) present(alert);
          });
        },
        document.hidden ? CLAIM_DELAY_HIDDEN_MS : CLAIM_DELAY_VISIBLE_MS,
      );
      timers.add(timer);
    };

    if (payments) {
      unsubscribes.push(
        notificationsSocketService.onPayinUpdate((payload) => {
          // The pesos of a USDT sale: the sale is not a collection to tell about.
          if (payload.status !== "CONFIRMED" || payload.announce === false) return;
          if (payload.rail !== "BREB") {
            announce(collectionPaidAlert(payload, text()), screenTokens.payin(payload.id));
            return;
          }
          if (!isIncomingPaymentCandidate(payload, null) || readBrebPayins.has(payload.id)) return;
          readBrebPayins.add(payload.id);
          PaymentsPayinService.getPayin(payload.id)
            .then(({ data }) => {
              if (!alive) return;
              const keyPayment = keyPaymentFor(data);
              if (keyPayment) {
                announce(brebKeyPaymentAlert(keyPayment, text()), [
                  ...screenTokens.brebKeyOf(keyPayment),
                  ...screenTokens.payin(payload.id),
                ]);
              } else {
                // A Bre-B QR with an amount: a collection like any other.
                announce(collectionPaidAlert(payload, text()), screenTokens.payin(payload.id));
              }
            })
            .catch(() => {
              readBrebPayins.delete(payload.id);
            });
        }),
        notificationsSocketService.onPayoutUpdate((payload) => {
          // A batch row (its batch tells) or a USDT purchase on the desk.
          if (payload.announce === false) return;
          announce(payoutAlert(payload, text()), screenTokens.payout(payload.id));
        }),
      );
    }
    if (payoutBatches) {
      unsubscribes.push(
        notificationsSocketService.onPayoutBatchUpdate((payload) => {
          announce(payoutBatchAlert(payload, text()), screenTokens.payoutBatch(payload.id));
        }),
      );
    }
    if (cards) {
      unsubscribes.push(
        notificationsSocketService.onCardActivity((payload) => {
          announce(cardActivityAlert(payload, text()), []);
        }),
      );
    }

    return () => {
      alive = false;
      unsubscribes.forEach((unsubscribe) => unsubscribe());
      timers.forEach(clearTimeout);
    };
    // The user is a dependency too: the socket service drops every listener when it disconnects, so a new
    // session in the same layout subscribes again.
  }, [payments, payoutBatches, cards, availableKey, user?.id]);
}
